/* ── Kangaroo Kourt: data model, table I/O, judge API and leaderboard math ── */

import { PHOTO_NAME_RE } from './photos';
import type { RosterEntry } from './roster';
import { ODATA_HEADERS, odataQuote, type TablesConfig } from './storage';

// Dev runs against the production table (the SAS is public config) and rows
// can never be deleted, so dev rows live in their own partitions. The server
// applies the same suffix via KOURT_PARTITION_SUFFIX.
const SUFFIX = import.meta.env.DEV ? '-dev' : '';
export const KOURT_PARTITION = {
  case: `case${SUFFIX}`,
  ruling: `ruling${SUFFIX}`,
  /** "Serve me by text" opt-ins — roster name + mobile, newest wins. */
  phone: `phone${SUFFIX}`,
} as const;

/** The judge's passcode, kept for the tab only — never localStorage. */
export const JUDGE_KEY = 'tlh-kourt-passcode';
export const THE_BENCH = 'The Bench';
export const JUDGE_NAME = 'The Less Than Honorable William Tisdale';
export const JUDGE_ROSTER_NAME = 'Bill Tisdale';

export type CaseKind = 'fine' | 'bonus';
export type Verdict = 'fined' | 'bonus' | 'dismissed';

/** A complaint on the docket. Anyone registered can file one. */
export interface KourtCase {
  rowKey: string;
  defendant: string;
  kind: CaseKind;
  /** What the filer recommends, in whole dollars. */
  amount: number;
  offense: string;
  filedBy: string;
  filedAt: string;
  /** Exhibit A: a file name in the photos blob folder (`<rowKey>.jpg`). */
  photoName?: string;
}

/** The Bench's decision. One per case, ever — the row key is the case key. */
export interface KourtRuling {
  caseKey: string;
  defendant: string;
  verdict: Verdict;
  /** Final amount. Zero when dismissed. */
  amount: number;
  opinion: string;
  ruledAt: string;
  /** A fine's summons to appear, a PDF in the photos blob folder. */
  summonsName?: string;
}

const SUMMONS_NAME_RE = /^[A-Za-z0-9._-]{1,80}\.pdf$/;

const LIMITS = { defendant: 60, offense: 300, opinion: 1000, filedBy: 40, amount: 500 } as const;
/** A complaint may recommend this much; only the Bench goes higher. */
export const COMPLAINT_MAX = 25;

/* ── Sanitizers (accept camelCase WS payloads and PascalCase table rows) ── */

type Raw = Record<string, unknown>;

function pick(e: Raw, camel: string, pascal: string): string | null {
  const v = typeof e[camel] === 'string' ? e[camel] : typeof e[pascal] === 'string' ? e[pascal] : null;
  return v as string | null;
}

function pickInt(e: Raw, camel: string, pascal: string): number | null {
  const v = e[camel] ?? e[pascal];
  const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : NaN;
  return Number.isInteger(n) && n >= 0 && n <= LIMITS.amount ? n : null;
}

export function toCase(raw: unknown): KourtCase | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const e = raw as Raw;
  const rowKey = pick(e, 'rowKey', 'RowKey');
  const defendant = pick(e, 'defendant', 'Defendant');
  const kind = pick(e, 'kind', 'Kind');
  const amount = pickInt(e, 'amount', 'Amount');
  const offense = pick(e, 'offense', 'Offense');
  const filedBy = pick(e, 'filedBy', 'FiledBy');
  const filedAt = pick(e, 'filedAt', 'FiledAt') ?? '';
  let photoName = pick(e, 'photoName', 'PhotoName') ?? '';
  if (photoName && !PHOTO_NAME_RE.test(photoName)) photoName = '';
  if (!rowKey || !defendant || (kind !== 'fine' && kind !== 'bonus') || amount === null) return null;
  if (!offense || !offense.trim() || !filedBy) return null;
  return {
    rowKey: rowKey.slice(0, 64),
    defendant: defendant.slice(0, LIMITS.defendant),
    kind,
    amount,
    offense: offense.slice(0, LIMITS.offense),
    filedBy: filedBy.slice(0, LIMITS.filedBy),
    filedAt,
    ...(photoName ? { photoName } : {}),
  };
}

export function toRuling(raw: unknown): KourtRuling | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const e = raw as Raw;
  const caseKey = pick(e, 'caseKey', 'RowKey');
  const defendant = pick(e, 'defendant', 'Defendant');
  const verdict = pick(e, 'verdict', 'Verdict');
  const amount = pickInt(e, 'amount', 'Amount');
  const opinion = pick(e, 'opinion', 'Opinion');
  const ruledAt = pick(e, 'ruledAt', 'RuledAt') ?? '';
  let summonsName = pick(e, 'summonsName', 'SummonsName') ?? '';
  if (summonsName && !SUMMONS_NAME_RE.test(summonsName)) summonsName = '';
  if (!caseKey || !defendant || amount === null || !opinion || !opinion.trim()) return null;
  if (verdict !== 'fined' && verdict !== 'bonus' && verdict !== 'dismissed') return null;
  return {
    caseKey: caseKey.slice(0, 64),
    defendant: defendant.slice(0, LIMITS.defendant),
    verdict,
    amount: verdict === 'dismissed' ? 0 : amount,
    opinion: opinion.slice(0, LIMITS.opinion),
    ruledAt,
    ...(summonsName ? { summonsName } : {}),
  };
}

/* ── Table I/O (browser side, read + add SAS) ─────────────────────────── */

/** One partition, whole. A season of fines is nowhere near the 1000-row
 *  page limit, so continuation tokens are deliberately ignored. */
async function fetchPartition(tables: TablesConfig, partition: string): Promise<unknown[] | null> {
  try {
    const filter = `PartitionKey eq '${odataQuote(partition)}'`;
    const response = await fetch(
      `${tables.endpoint}/messages()?$filter=${encodeURIComponent(filter)}&${tables.messagesSas}`,
      { headers: ODATA_HEADERS },
    );
    if (!response.ok) return null;
    const body = await response.json();
    return Array.isArray(body?.value) ? body.value : null;
  } catch {
    return null;
  }
}

export async function fetchCases(tables: TablesConfig): Promise<KourtCase[] | null> {
  const rows = await fetchPartition(tables, KOURT_PARTITION.case);
  return rows ? rows.map(toCase).filter((c): c is KourtCase => c !== null) : null;
}

export async function fetchRulings(tables: TablesConfig): Promise<KourtRuling[] | null> {
  const rows = await fetchPartition(tables, KOURT_PARTITION.ruling);
  return rows ? rows.map(toRuling).filter((r): r is KourtRuling => r !== null) : null;
}

export async function insertCase(tables: TablesConfig, c: KourtCase): Promise<boolean> {
  try {
    const entity: Record<string, string | number> = {
      PartitionKey: KOURT_PARTITION.case,
      RowKey: c.rowKey,
      Defendant: c.defendant,
      Kind: c.kind,
      Amount: c.amount,
      Offense: c.offense,
      FiledBy: c.filedBy,
      FiledAt: c.filedAt,
    };
    if (c.photoName) entity.PhotoName = c.photoName;
    const response = await fetch(`${tables.endpoint}/messages?${tables.messagesSas}`, {
      method: 'POST',
      headers: { ...ODATA_HEADERS, 'Content-Type': 'application/json', Prefer: 'return-no-content' },
      body: JSON.stringify(entity),
    });
    return response.ok;
  } catch {
    return false;
  }
}

/* ── "Serve me by text": a roster name and a mobile number ─────────────── */

export interface PhoneOptIn {
  rowKey: string;
  email: string;
  rosterName: string;
  /** E.164, US only: +1 and ten digits. */
  phone: string;
}

/** Ten US digits (optionally with a leading 1) → +1XXXXXXXXXX, else null. */
export function normalizePhone(input: string): string | null {
  const digits = input.replace(/\D/g, '');
  const ten = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  return /^[2-9]\d{9}$/.test(ten) ? `+1${ten}` : null;
}

/** +17705551212 → (770) 555-1212 */
export function formatPhone(e164: string): string {
  const d = e164.replace(/\D/g, '').slice(-10);
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : e164;
}

function toPhoneOptIn(raw: unknown): PhoneOptIn | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const e = raw as Raw;
  const rowKey = pick(e, 'rowKey', 'RowKey');
  const email = pick(e, 'email', 'Email');
  const rosterName = pick(e, 'rosterName', 'RosterName');
  const phone = pick(e, 'phone', 'Phone');
  if (!rowKey || !email || !rosterName || !phone || !/^\+1\d{10}$/.test(phone)) return null;
  return { rowKey, email, rosterName: rosterName.slice(0, LIMITS.defendant), phone };
}

/** The newest opt-in filed under this email, or null. Rows are add-only, so
 *  changing a number means filing a fresh row; the inverted-timestamp key
 *  sorts the latest first. */
export async function fetchPhoneOptIn(tables: TablesConfig, email: string): Promise<PhoneOptIn | null> {
  try {
    const filter = `PartitionKey eq '${odataQuote(KOURT_PARTITION.phone)}' and Email eq '${odataQuote(email)}'`;
    const response = await fetch(
      `${tables.endpoint}/messages()?$filter=${encodeURIComponent(filter)}&${tables.messagesSas}`,
      { headers: ODATA_HEADERS },
    );
    if (!response.ok) return null;
    const body = await response.json();
    const rows = (Array.isArray(body?.value) ? body.value : [])
      .map(toPhoneOptIn)
      .filter((r: PhoneOptIn | null): r is PhoneOptIn => r !== null)
      .sort((a: PhoneOptIn, b: PhoneOptIn) => a.rowKey.localeCompare(b.rowKey));
    return rows[0] ?? null;
  } catch {
    return null;
  }
}

export async function insertPhoneOptIn(tables: TablesConfig, optIn: PhoneOptIn): Promise<boolean> {
  try {
    const response = await fetch(`${tables.endpoint}/messages?${tables.messagesSas}`, {
      method: 'POST',
      headers: { ...ODATA_HEADERS, 'Content-Type': 'application/json', Prefer: 'return-no-content' },
      body: JSON.stringify({
        PartitionKey: KOURT_PARTITION.phone,
        RowKey: optIn.rowKey,
        Email: optIn.email,
        RosterName: optIn.rosterName,
        Phone: optIn.phone,
      }),
    });
    return response.ok;
  } catch {
    return false;
  }
}

/** Union by key; incoming wins. Order is left to the callers' sorts. */
export function mergeByKey<T>(current: T[], incoming: T[], key: (item: T) => string): T[] {
  const byKey = new Map<string, T>();
  for (const item of [...current, ...incoming]) byKey.set(key(item), item);
  return [...byKey.values()];
}

/* ── The judge's API (/api/kourt, passcode checked server-side) ────────── */

export type KourtResult<T> = ({ ok: true } & T) | { ok: false; status: number; reason: string };

async function callKourt<T>(body: Record<string, unknown>): Promise<KourtResult<T>> {
  try {
    const response = await fetch('/api/kourt', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = (await response.json().catch(() => ({}))) as Record<string, unknown>;
    if (response.ok && data.ok === true) return { ...(data as T), ok: true };
    return {
      ok: false,
      status: response.status,
      reason: typeof data.reason === 'string' ? data.reason : `http ${response.status}`,
    };
  } catch {
    return { ok: false, status: 0, reason: 'unreachable' };
  }
}

/** Whether a gavel has been issued (passcode + storage configured server-side). */
export async function kourtStatus(): Promise<boolean> {
  try {
    const response = await fetch('/api/kourt');
    if (!response.ok) return false;
    const data = (await response.json()) as { configured?: unknown };
    return data.configured === true;
  } catch {
    return false;
  }
}

export function verifyJudge(passcode: string): Promise<KourtResult<object>> {
  return callKourt({ action: 'verify', passcode });
}

export interface RulingInput {
  caseKey: string;
  verdict: Verdict;
  amount: number;
  opinion: string;
}

export function ruleOnCase(
  passcode: string,
  input: RulingInput,
): Promise<KourtResult<{ ruling: KourtRuling; emailed: boolean; texted: boolean }>> {
  return callKourt({ action: 'rule', passcode, ...input });
}

export interface SuaSponteInput {
  defendant: string;
  verdict: 'fined' | 'bonus';
  amount: number;
  offense: string;
  opinion: string;
}

export function ruleSuaSponte(
  passcode: string,
  input: SuaSponteInput,
): Promise<KourtResult<{ case: KourtCase; ruling: KourtRuling; emailed: boolean; texted: boolean }>> {
  return callKourt({ action: 'sua-sponte', passcode, ...input });
}

/* ── Leaderboard math (pure) ───────────────────────────────────────────── */

export interface Standing {
  name: string;
  /** Fines minus credits. Positive means he owes the fund. */
  owed: number;
  fineCount: number;
  fineTotal: number;
  bonusCount: number;
  bonusTotal: number;
  dismissedCount: number;
  onRoster: boolean;
  rank: number;
  title: string;
}

export function rankTitle(rank: number, owed: number, hasHistory: boolean): string {
  if (owed < 0) return 'Model Citizen';
  if (owed === 0) return hasHistory ? 'Square With the Kourt' : 'Clean Record (for now)';
  if (rank === 1) return 'Public Enemy No. 1';
  if (rank === 2) return 'Menace to Society';
  if (rank === 3) return 'Habitual Offender';
  return 'Person of Interest';
}

/** Everyone on the roster plus anyone the Kourt has ever ruled on, ranked by
 *  what they owe. Names are exact strings — no fuzzy matching, ever. */
export function computeStandings(rulings: KourtRuling[], roster: RosterEntry[]): Standing[] {
  const rosterNames = new Set(roster.map((p) => p.name));
  const tally = new Map<string, Standing>();
  const blank = (name: string): Standing => ({
    name,
    owed: 0,
    fineCount: 0,
    fineTotal: 0,
    bonusCount: 0,
    bonusTotal: 0,
    dismissedCount: 0,
    onRoster: rosterNames.has(name),
    rank: 0,
    title: '',
  });
  for (const name of rosterNames) tally.set(name, blank(name));
  for (const r of rulings) {
    const s = tally.get(r.defendant) ?? blank(r.defendant);
    if (r.verdict === 'fined') {
      s.fineCount += 1;
      s.fineTotal += r.amount;
    } else if (r.verdict === 'bonus') {
      s.bonusCount += 1;
      s.bonusTotal += r.amount;
    } else {
      s.dismissedCount += 1;
    }
    tally.set(r.defendant, s);
  }
  const standings = [...tally.values()].map((s) => ({ ...s, owed: s.fineTotal - s.bonusTotal }));
  standings.sort(
    (a, b) => b.owed - a.owed || b.fineCount - a.fineCount || a.name.localeCompare(b.name),
  );
  return standings.map((s, i) => ({
    ...s,
    rank: i + 1,
    title: rankTitle(i + 1, s.owed, s.fineCount + s.bonusCount + s.dismissedCount > 0),
  }));
}

export function mojitoFund(rulings: KourtRuling[]): { gross: number; credits: number; net: number } {
  let gross = 0;
  let credits = 0;
  for (const r of rulings) {
    if (r.verdict === 'fined') gross += r.amount;
    else if (r.verdict === 'bonus') credits += r.amount;
  }
  return { gross, credits, net: gross - credits };
}

/** Complaints nobody has ruled on, newest first (row keys are inverted timestamps). */
export function pendingDocket(cases: KourtCase[], rulings: KourtRuling[]): KourtCase[] {
  const ruled = new Set(rulings.map((r) => r.caseKey));
  return cases.filter((c) => !ruled.has(c.rowKey)).sort((a, b) => a.rowKey.localeCompare(b.rowKey));
}

export interface RecordEntry {
  ruling: KourtRuling;
  /** Missing only if the case row failed to load; the ruling still stands. */
  filed?: KourtCase;
}

/** Every ruling, newest first, paired with the complaint it answered. */
export function kourtRecord(cases: KourtCase[], rulings: KourtRuling[]): RecordEntry[] {
  const byKey = new Map(cases.map((c) => [c.rowKey, c]));
  return rulings
    .map((ruling) => ({ ruling, filed: byKey.get(ruling.caseKey) }))
    .sort((a, b) => b.ruling.ruledAt.localeCompare(a.ruling.ruledAt));
}

export function rapSheet(name: string, cases: KourtCase[], rulings: KourtRuling[]): RecordEntry[] {
  return kourtRecord(cases, rulings).filter((e) => e.ruling.defendant === name);
}

/** "$12", "−$3" — sign shown, never a decimal; the Kourt deals in whole dollars. */
export function formatDollars(n: number): string {
  return n < 0 ? `−$${Math.abs(n)}` : `$${n}`;
}

/* ── Sentencing guidelines & the judge's stock phrases ─────────────────── */

export interface Guideline {
  label: string;
  kind: CaseKind;
  amount: number;
  offense: string;
}

/** Quick-picks for the complaint form. Nothing here binds the Bench. */
export const SENTENCING_GUIDELINES: readonly Guideline[] = [
  { label: 'No balls', kind: 'fine', amount: 2, offense: 'Showed up to a match without a can of balls' },
  { label: 'Bad line call', kind: 'fine', amount: 5, offense: 'Called a ball out that was in by a foot. Everyone saw it.' },
  { label: 'Late to warm-up', kind: 'fine', amount: 3, offense: 'Arrived after warm-up started and asked what the score was' },
  { label: 'Ghosted the captain', kind: 'fine', amount: 5, offense: 'Made the captain chase him for availability' },
  { label: 'Tanked a tiebreak', kind: 'fine', amount: 3, offense: 'Double-faulted the last two points of a tiebreak' },
  { label: 'Spoke ill of Clemson', kind: 'fine', amount: 10, offense: 'Spoke ill of the Clemson Tigers within earshot of the Bench' },
  { label: 'Go Gamecocks', kind: 'fine', amount: 25, offense: 'Said "Go Gamecocks." On purpose. In the presence of the Bench.' },
  { label: 'Asked for a refund', kind: 'fine', amount: 5, offense: 'Requested a refund from the Kourt, which is itself an offense (Rule 3)' },
  { label: 'Brought the mojitos', kind: 'bonus', amount: 5, offense: 'Brought mojito supplies to a match without being asked' },
  { label: 'Won a third-set breaker', kind: 'bonus', amount: 3, offense: 'Won a third-set tiebreak with the whole team watching' },
  { label: 'Go Tigers', kind: 'bonus', amount: 2, offense: 'Said "Go Tigers" unprompted' },
  { label: 'Early availability', kind: 'bonus', amount: 1, offense: 'Put in availability before the captain had to ask' },
  { label: 'Hosted the cookout', kind: 'bonus', amount: 10, offense: 'Hosted or grilled at the home-match cookout' },
];

/** Openers the judge can drop into an opinion before making it his own. */
export const OPINION_OPENERS: readonly string[] = [
  "The Kourt has considered the defendant's excuse and finds it hilarious.",
  'Counsel for the defense was not present, which the Kourt notes is typical.',
  'The Kourt takes judicial notice that everyone saw it.',
  'Having reviewed the evidence, the group chat, and the vibes, the Kourt rules as follows:',
  'Go Tigers. Now then.',
  'The defendant is reminded that this Kourt does not hear appeals, and would not hear this one if it did.',
  "In light of the defendant's service to the mojito fund, the Kourt is moved. Slightly.",
];
